import { Alert, Button, Checkbox, FormControlLabel, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { PasswordChecklist } from 'components/shared/PasswordChecklist';
import { PasswordField } from 'components/shared/PasswordField';
import { observer } from 'mobx-react-lite';
import { KeyboardEvent, useEffect } from 'react';
import { CreateUserStore } from 'stores/users/CreateUserStore';
import { createUser as createUserLabels } from 'utils/labels';

type CreateUserProps = {
  scrollToTop: () => void;
  createUserStore: CreateUserStore;
};

const CreateUser = observer(({ scrollToTop, createUserStore }: CreateUserProps) => {
  const { form, message, status, createUser } = createUserStore;
  const passwordField = form.$('password');

  useEffect(() => {
    if (message) scrollToTop();
  }, [message, scrollToTop]);

  const handleKeyInField = (evt: KeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.stopPropagation();
      evt.preventDefault();
      if (form.isValid) {
        createUser();
      }
    }
  };

  return (
    <div aria-label="create user page" style={{ width: 400, marginTop: 60, marginBottom: 30 }}>
      <form autoComplete="off">
        <Stack spacing={4}>
          <Typography variant="h5">
            <strong>Create User</strong>
          </Typography>
          {message && (
            <Alert aria-label="form-error" severity={status === 'error' ? 'error' : 'success'}>
              {message}
            </Alert>
          )}
          <TextField
            variant="outlined"
            autoComplete="off"
            onKeyDown={handleKeyInField}
            data-testid="username"
            required
            {...form.$('username').bind()}
          />
          <PasswordField
            variant="outlined"
            autoComplete="new-password"
            onKeyDown={handleKeyInField}
            data-testid="password"
            required
            {...passwordField.bind()}
            inputProps={{
              role: 'textbox',
            }}
            helperText={undefined}
          />
          <PasswordChecklist style={{ marginTop: '16px' }} password={passwordField.value} />
          <TextField
            variant="outlined"
            autoComplete="off"
            onKeyDown={handleKeyInField}
            data-testid="email"
            required
            {...form.$('email').bind()}
          />
          <Stack spacing={2}>
            <div>
              <Tooltip title="User can log in" placement="top-start">
                <FormControlLabel
                  control={<Checkbox data-testid="enabled" {...form.$('enabled').bind()} />}
                  label={createUserLabels.enabled}
                />
              </Tooltip>
            </div>
            <div>
              <Tooltip title="User can edit settings for non-internal users" placement="top-start">
                <FormControlLabel
                  control={<Checkbox data-testid="internal" {...form.$('internal').bind()} />}
                  label={createUserLabels.internal}
                />
              </Tooltip>
            </div>
            <div>
              <Tooltip title="User can edit other users" placement="top-start">
                <FormControlLabel
                  control={<Checkbox data-testid="admin" {...form.$('admin').bind()} />}
                  label={createUserLabels.admin}
                />
              </Tooltip>
            </div>
          </Stack>

          <Button
            variant="contained"
            disableElevation
            style={{ height: 56 }}
            disabled={!form.isValid}
            onClick={() => createUser()}
          >
            Create User
          </Button>
        </Stack>
      </form>
    </div>
  );
});

export { CreateUser };
